/**
 * GlossaryHover — wraps a plain-text string and replaces every
 * known acronym with a hover-tooltip showing the expansion.
 *
 * Glossary comes from analysis.summary.glossary built at parse time
 * by services/rfp_pipeline._extract_glossary (regex over full text).
 *
 * Renders inline-only (no block wrappers) so it can drop into any
 * paragraph / list-item / summary line.
 */
import React, { useMemo } from "react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * @param {string} text — the prose to scan for acronyms.
 * @param {Record<string,string>} glossary — {"LOA": "Letter of Award"}
 * @param {string} className — applied to the wrapping span.
 */
export default function GlossaryHover({ text, glossary, className = "" }) {
  // Build a single anchored regex of all acronyms (capital-letter
  // word boundaries). Memoised on the glossary keys so it doesn't
  // rebuild every render.
  const { regex, lookup } = useMemo(() => {
    if (!glossary || typeof glossary !== "object") {
      return { regex: null, lookup: {} };
    }
    const keys = Object.keys(glossary).filter((k) => k && k.length >= 2);
    if (keys.length === 0) return { regex: null, lookup: glossary };
    // Sort by length desc so "DSC III" matches before "DSC" — though
    // glossary keys are typically single tokens.
    keys.sort((a, b) => b.length - a.length);
    const escaped = keys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    return {
      // Word boundary on both sides; capture group preserves the
      // original casing for re-rendering.
      regex: new RegExp(`\\b(${escaped.join("|")})\\b`, "g"),
      lookup: glossary,
    };
  }, [glossary]);

  if (!text) return null;
  if (!regex) return <span className={className}>{text}</span>;

  const parts = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  // Reset regex state across renders.
  regex.lastIndex = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(<span key={`t-${key++}`}>{text.slice(lastIdx, m.index)}</span>);
    }
    const acronym = m[1];
    const expansion = lookup[acronym];
    parts.push(
      <TooltipProvider key={`a-${key++}`} delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="underline decoration-dotted decoration-slate-400 cursor-help">
              {acronym}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">
            <strong className="font-semibold">{acronym}</strong>: {expansion}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
    lastIdx = m.index + acronym.length;
    // Defensive against catastrophic regex looping on zero-length matches.
    if (m.index === regex.lastIndex) regex.lastIndex += 1;
  }
  if (lastIdx < text.length) {
    parts.push(<span key={`t-${key++}`}>{text.slice(lastIdx)}</span>);
  }
  return <span className={className}>{parts}</span>;
}
